/**
 * Investor-Portal — Cashflow (#1189 v2.571.0 → #1191 v2.573.0).
 *
 * 6 Bausteine:
 *   1. Liquiditäts-Cockpit       — Bank-Saldo Start/Ende + Veränderung pro Konto
 *   2. Geldpuls                  — Monatstrend operativ/investiv/finanzierend
 *   3. CF-Rechnung               — Direkte (aus Bank) + Indirekte (Plan) Side-by-Side
 *   4. Cashflow-Wasserfall       — Bank-Start → ±Buckets → Bank-Ende
 *   5. Top-Einzelbewegungen      — Top-30 Bank-Movements + Bucket-Hotspot
 *   6. Liquiditäts-Runway        — Months-of-Runway aus Ø Operativ-Verbrauch
 *
 * #1191: Klassifikation auf Odoo `account.account.tag_ids` umgestellt
 *   (Tags 1/2/3 = Operating/Financing/Investing+Extraordinary). Tagless
 *   Konten fallen auf UGB-Klassen-Heuristik + Overrides zurück.
 *
 * Plus:
 *   - Offene CC-Auslagen (3720/3725 Saldo zum Periodenende)
 *   - Versteckte interne Transfers werden als „Intern" gebucht (2772/2701/2800/2802 als Gegenkonto)
 *
 * Read-only Reporting. Direkte Methode rekonziliert exakt 1:1 zum Bank-Delta.
 * Indirekte Plan-Methode (Jahresüberschuss + Adjustments) wird daneben gezeigt —
 * keine 1:1-Brücke, weil die Methoden strukturell anders rechnen.
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ComposedChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, ReferenceLine, LabelList, LineChart, Line,
} from 'recharts';
import { Wallet, Activity, TrendingDown, ArrowRight, AlertCircle, CreditCard } from 'lucide-react';
import PeriodToggle, { buildPeriodQuery } from './components/PeriodToggle';
import DataMaturityBanner from './components/DataMaturityBanner';
import { MaturityOverlay, reifeFooter } from './components/MaturityChartOverlay';
import { defaultMatureMonthState, applyYtdCutoff } from '../../utils/data-maturity';

const API = import.meta.env.VITE_API_URL || '';
const MONTH_LABELS = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];

const BUCKET_META = {
  operativ:     { label: 'Operativ',         color: '#338edc', tint: '#EFF6FF' },
  investiv:     { label: 'Investiv',         color: '#6366F1', tint: '#EEF2FF' },
  finanzierend: { label: 'Finanzierend',     color: '#22D3EE', tint: '#ECFEFF' },
  intern:       { label: 'Intern (Übertrag)', color: '#9CA3AF', tint: '#F3F4F6' },
};

function fmtEur(v) {
  if (v == null || isNaN(v)) return '–';
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
}
function fmtEurDetail(v) {
  if (v == null || isNaN(v)) return '–';
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(v);
}
function fmtDate(iso) {
  if (!iso) return '–';
  return iso.slice(8, 10) + '.' + iso.slice(5, 7) + '.';
}

export default function InvestorCashflow() {
  const [searchParams] = useSearchParams();

  const [periodState, setPeriodState] = useState(() => {
    const mature = defaultMatureMonthState();
    return {
      period: searchParams.get('period') || 'month',
      year: parseInt(searchParams.get('year') || mature.year, 10),
      month: parseInt(searchParams.get('month') || mature.month, 10),
      quarter: parseInt(searchParams.get('quarter') || Math.ceil((new Date().getMonth() + 1) / 3), 10),
      fromDate: searchParams.get('from_date') || null,
      toDate: searchParams.get('to_date') || null,
    };
  });

  const [data, setData] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const effectivePeriodState = useMemo(() => applyYtdCutoff(periodState), [periodState]);
  const periodQuery = useMemo(() => buildPeriodQuery(effectivePeriodState), [effectivePeriodState]);

  useEffect(() => {
    let abort = false;
    async function load() {
      setLoading(true); setError(null);
      try {
        const q = new URLSearchParams(periodQuery).toString();
        const [r1, r2] = await Promise.all([
          fetch(`${API}/api/investor/cashflow/baseline?${q}`, { credentials: 'include' }),
          fetch(`${API}/api/investor/cashflow/plan?${q}`, { credentials: 'include' }),
        ]);
        const j1 = await r1.json();
        const j2 = await r2.json();
        if (abort) return;
        if (!r1.ok) { setError(j1.error || `HTTP ${r1.status}`); setData(null); }
        else setData(j1);
        if (r2.ok) setPlan(j2); else setPlan(null);
      } catch (e) {
        if (!abort) setError(e.message);
      } finally {
        if (!abort) setLoading(false);
      }
    }
    load();
    return () => { abort = true; };
  }, [periodQuery]);

  // Bucket-Lookup (operativ kann fehlen wenn 0 Lines)
  const bucketByKey = useMemo(() => {
    const m = {};
    for (const b of (data?.direct.buckets || [])) m[b.bucket] = b;
    return m;
  }, [data]);

  // Wasserfall: Start → Operativ → Investiv → Finanzierend → Intern → Ende
  const waterfallData = useMemo(() => {
    if (!data) return [];
    const order = ['operativ', 'investiv', 'finanzierend', 'intern'];
    const rows = [];
    let cum = data.cockpit.total_start;
    rows.push({ name: 'Bank Start', kind: 'total', base: 0, delta: cum, signed: cum, cumulative: cum });
    for (const k of order) {
      const b = bucketByKey[k];
      if (!b || b.net === 0) continue;
      const start = cum;
      cum += b.net;
      rows.push({
        name: BUCKET_META[k].label,
        kind: 'bucket',
        bucket: k,
        base: b.net >= 0 ? start : cum,
        delta: Math.abs(b.net),
        signed: b.net,
        cumulative: cum,
      });
    }
    rows.push({ name: 'Bank Ende', kind: 'total', base: 0, delta: cum, signed: cum, cumulative: cum });
    return rows;
  }, [data, bucketByKey]);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A1A2E]">Cashflow</h1>
          <p className="text-sm text-[#6B7280] mt-1">
            Direkte Methode (Bank-Bewegungen) + Indirekte Methode (Plan).
            Klassifikation via Odoo-Tags{' '}
            <span className="font-mono text-[11px]">account.account.tag_ids</span>
            {' '}+ Override-Liste.
          </p>
        </div>
        <PeriodToggle
          period={periodState.period}
          year={periodState.year}
          month={periodState.month}
          quarter={periodState.quarter}
          fromDate={periodState.fromDate}
          toDate={periodState.toDate}
          onChange={setPeriodState}
        />
      </div>

      <DataMaturityBanner periodState={periodState} />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-900 text-sm px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle width={16} height={16} /> {error}
        </div>
      )}

      {/* ─── Baustein 1: Liquiditäts-Cockpit ─────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <KpiTile label="Bank-Saldo (Start)" Icon={Wallet} iconColor="#6B7280"
          value={fmtEur(data?.cockpit.total_start)} loading={loading}
          sub={data?.period ? `vor ${data.period.dateFrom}` : null} />
        <KpiTile label="Bank-Saldo (Ende)" Icon={Wallet} iconColor="#338edc" iconTint="#EFF6FF"
          value={fmtEur(data?.cockpit.total_end)} loading={loading}
          sub={data?.period ? `bis ${data.period.dateTo}` : null} highlight />
        <KpiTile label="Δ Bank Periode" Icon={Activity}
          iconColor={(data?.cockpit.delta || 0) >= 0 ? '#10B981' : '#EF4444'}
          iconTint={(data?.cockpit.delta || 0) >= 0 ? '#ECFDF5' : '#FEF2F2'}
          value={fmtEur(data?.cockpit.delta)} loading={loading}
          sub="Veränderung Liquidität" />
        <RunwayTile runway={data?.runway} loading={loading} />
      </div>

      {/* Banken-Detail */}
      {data?.cockpit.banks && data.cockpit.banks.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
          <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-2">Bank-Konten</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] text-[#6B7280] uppercase border-b border-[#E5E7EB]">
                <th className="py-2 pr-3">Konto</th>
                <th className="py-2 px-2 text-right">Start</th>
                <th className="py-2 px-2 text-right">Ende</th>
                <th className="py-2 pl-2 text-right">Δ</th>
              </tr>
            </thead>
            <tbody>
              {data.cockpit.banks.map(b => (
                <tr key={b.id} className="border-b border-[#F3F4F6] last:border-b-0">
                  <td className="py-2 pr-3">
                    <span className="font-mono text-[12px] text-[#6B7280] mr-2">{b.code}</span>
                    {b.name}
                  </td>
                  <td className="py-2 px-2 text-right tabular-nums">{fmtEurDetail(b.start)}</td>
                  <td className="py-2 px-2 text-right tabular-nums font-medium text-[#1A1A2E]">{fmtEurDetail(b.end)}</td>
                  <td className="py-2 pl-2 text-right tabular-nums" style={{ color: b.delta >= 0 ? '#10B981' : '#EF4444' }}>
                    {b.delta >= 0 ? '+' : ''}{fmtEurDetail(b.delta)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── Baustein 4: Cashflow-Wasserfall ──────────────────── */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="text-sm font-semibold text-[#1A1A2E]">Cashflow-Wasserfall</h2>
          <span className="text-[11px] text-[#9CA3AF]">Bank-Start → ±Buckets → Bank-Ende</span>
        </div>
        {loading ? (
          <div className="h-[320px] bg-[#F3F4F6] rounded animate-pulse" />
        ) : waterfallData.length > 0 ? (
          <WaterfallChart waterfallData={waterfallData} />
        ) : (
          <div className="text-sm text-[#9CA3AF] text-center py-12">Keine Bewegungen im Zeitraum.</div>
        )}
        <div className="text-[11px] text-[#9CA3AF] mt-2">
          Reconciliation Direkt vs Bank-Δ: {data ? fmtEurDetail((data.cockpit.delta || 0) - (data.direct.total_net || 0)) : '–'}
          {' '}(sollte 0 sein)
        </div>
      </div>

      {/* ─── Baustein 2: Geldpuls (Monatstrend) ──────────────── */}
      {data?.monats_trend && data.monats_trend.length > 1 && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
          <div className="flex items-baseline justify-between mb-2">
            <h2 className="text-sm font-semibold text-[#1A1A2E]">Geldpuls — Monatstrend</h2>
            <span className="text-[11px] text-[#9CA3AF]">Operativ / Investiv / Finanzierend (exkl. Intern)</span>
          </div>
          <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
              <LineChart data={data.monats_trend.map(m => ({ ...m, label: MONTH_LABELS[m.monat - 1] }))}
                margin={{ top: 12, right: 16, left: 0, bottom: 4 }}>
                {MaturityOverlay({ year: periodState.year, monthLabels: MONTH_LABELS })}
                <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={false}
                  tickFormatter={v => new Intl.NumberFormat('de-AT', { notation: 'compact', maximumFractionDigits: 1 }).format(v) + ' €'} />
                <ReferenceLine y={0} stroke="#9CA3AF" />
                <Tooltip formatter={(v) => fmtEurDetail(v)}
                  contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="operativ" stroke="#338edc" strokeWidth={2.5} dot={{ r: 3 }} name="Operativ" />
                <Line type="monotone" dataKey="investiv" stroke="#6366F1" strokeWidth={2} dot={{ r: 3 }} name="Investiv" />
                <Line type="monotone" dataKey="finanzierend" stroke="#22D3EE" strokeWidth={2} dot={{ r: 3 }} name="Finanzierend" />
              </LineChart>
            </ResponsiveContainer>
          </div>
          {reifeFooter(periodState.year) && (
            <div className="text-[11px] text-[#9CA3AF] mt-1">{reifeFooter(periodState.year)}</div>
          )}
        </div>
      )}

      {/* ─── Baustein 3: CF-Rechnung — 2 Brücken untereinander (#1193) ──── */}
      <div className="space-y-3">
        {/* Brücke 1 — Direkte Methode (echte Bankflüsse) */}
        <CashflowBridge
          title="Direkte Methode"
          subtitle="echte Bankflüsse"
          meta={data ? `${data.direct.lines_processed} Bank-Lines · ${data.direct.moves_processed} Moves` : null}
          loading={loading}
          items={(data?.direct.buckets || []).map(b => ({
            key: b.bucket,
            label: BUCKET_META[b.bucket]?.label || b.label,
            value: b.net,
            color: b.bucket === 'intern' ? BUCKET_META.intern.color : (b.net >= 0 ? '#10B981' : '#EF4444'),
            tone: b.bucket === 'intern' ? 'neutral' : (b.net >= 0 ? 'positive' : 'negative'),
            sub: `${b.lines} Lines`,
          }))}
          totalLabel="Σ Δ Liquidität"
          totalValue={data?.direct.total_net}
          totalSub={`ohne Intern: ${fmtEurDetail(data?.direct.total_net_excl_intern)}`}
          emptyHint="Keine Bank-Bewegungen im Zeitraum."
        />

        {/* Brücke 2 — Indirekte Methode (Plan) */}
        <CashflowBridge
          title="Indirekte Methode (Plan)"
          subtitle="Jahresüberschuss + Anpassungen"
          meta={plan ? `${plan.positions?.length || 0} Plan-Positionen` : null}
          loading={loading}
          items={(plan?.aggregates || []).map(a => ({
            key: a.bucket,
            label: BUCKET_META[a.bucket]?.label || a.label,
            value: a.total,
            color: a.total >= 0 ? '#10B981' : '#EF4444',
            tone: a.total >= 0 ? 'positive' : 'negative',
            sub: a.source === 'detail_sum' ? 'Σ aus Details' : null,
          }))}
          totalLabel="Σ Δ Liquidität Plan"
          totalValue={plan?.delta_liquide_mittel}
          emptyHint={(
            <>Keine Plan-Werte in <span className="font-mono text-[11px]">investor_plan_values</span> für diesen Zeitraum.</>
          )}
          footerNote="ℹ️ Aggregate aus Σ-Positionen des Plans. Details aufklappen für alle Einzelzeilen."
          detailsAccordion={plan && plan.positions?.length > 0 ? (
            <PlanDetailsAccordion positions={plan.positions} labelsSource={plan.labels_source} />
          ) : null}
        />

        {/* Hinweis-Zeile: Methoden-Asymmetrie */}
        <div className="text-[11px] text-[#9CA3AF] px-1">
          Direkte Methode = echte Bankflüsse aus <span className="font-mono text-[10px]">account.move.line</span>.{' '}
          Indirekte Methode = Plan (Jahresüberschuss + Anpassungen) aus <span className="font-mono text-[10px]">investor_plan_values</span>.{' '}
          Keine 1:1-Brücke — nur Aggregate vergleichbar.
        </div>
      </div>

      {/* ─── Baustein 5: Top-30 Einzelbewegungen + Bucket-Hotspots ──── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 bg-white border border-[#E5E7EB] rounded-xl p-4">
          <div className="flex items-baseline justify-between mb-2">
            <h2 className="text-sm font-semibold text-[#1A1A2E]">Top-30 Einzelbewegungen</h2>
            <span className="text-[11px] text-[#9CA3AF]">nach |Betrag| · Gegenkonto = dominanter Anteil</span>
          </div>
          {loading ? (
            <div className="h-64 bg-[#F3F4F6] rounded animate-pulse" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-left text-[10px] text-[#6B7280] uppercase border-b border-[#E5E7EB]">
                    <th className="py-1.5 pr-2">Datum</th>
                    <th className="py-1.5 px-2">Partner / Beschreibung</th>
                    <th className="py-1.5 px-2">Gegenkonto</th>
                    <th className="py-1.5 px-2">Bucket</th>
                    <th className="py-1.5 pl-2 text-right">Betrag</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.top_movements || []).map(m => (
                    <tr key={m.bank_line_id} className="border-b border-[#F3F4F6] last:border-b-0">
                      <td className="py-1.5 pr-2 tabular-nums text-[#6B7280]">{fmtDate(m.date)}</td>
                      <td className="py-1.5 px-2 text-[#1A1A2E]">
                        <div className="truncate max-w-[260px]" title={m.partner_name || m.name || ''}>
                          {m.partner_name || m.name || '—'}
                        </div>
                        {m.ref && <div className="text-[10px] text-[#9CA3AF] truncate max-w-[260px]">{m.ref}</div>}
                      </td>
                      <td className="py-1.5 px-2 text-[#6B7280]">
                        <span className="font-mono text-[11px]">{m.gegenkonto_code || '–'}</span>
                        {m.gegenkonto_name && <div className="text-[10px] text-[#9CA3AF] truncate max-w-[140px]">{m.gegenkonto_name}</div>}
                      </td>
                      <td className="py-1.5 px-2">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                          style={{ backgroundColor: BUCKET_META[m.bucket]?.tint, color: BUCKET_META[m.bucket]?.color }}>
                          {BUCKET_META[m.bucket]?.label || m.bucket}
                        </span>
                      </td>
                      <td className="py-1.5 pl-2 text-right tabular-nums font-medium"
                        style={{ color: m.amount >= 0 ? '#10B981' : '#EF4444' }}>
                        {m.amount >= 0 ? '+' : ''}{fmtEurDetail(m.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Bucket-Hotspot: Top-5 Codes pro Bucket */}
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
          <div className="text-sm font-semibold text-[#1A1A2E] mb-2">Hotspot pro Bucket</div>
          {loading ? (
            <div className="h-64 bg-[#F3F4F6] rounded animate-pulse" />
          ) : (
            <div className="space-y-3">
              {(data?.direct.buckets || []).map(b => (
                <div key={b.bucket}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] font-medium uppercase tracking-wider"
                      style={{ color: BUCKET_META[b.bucket]?.color }}>
                      {BUCKET_META[b.bucket]?.label || b.label}
                    </span>
                    <span className="text-[10px] text-[#9CA3AF] tabular-nums">{fmtEur(b.net)}</span>
                  </div>
                  {(b.top_codes || []).slice(0, 5).map(c => (
                    <div key={c.code} className="flex items-center justify-between text-[11px] py-0.5">
                      <div className="truncate flex-1 mr-2">
                        <span className="font-mono text-[10px] text-[#9CA3AF] mr-1">{c.code}</span>
                        <span className="text-[#4B5563]">{c.name}</span>
                      </div>
                      <span className="tabular-nums text-[#1A1A2E]">{fmtEur(c.amount)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ─── Offene CC-Auslagen (Mini-Card) ──────────────────── */}
      {data?.cc_pending && data.cc_pending.per_account.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2">
              <CreditCard width={18} height={18} className="text-amber-700 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-amber-900">Offene Kreditkarten-Auslagen</div>
                <div className="text-[11px] text-amber-700 mt-0.5">Saldo Konten 3720/3725 zum Periodenende — noch nicht über die Bank gerechnet.</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-lg font-semibold text-amber-900 tabular-nums">{fmtEurDetail(data.cc_pending.total)}</div>
            </div>
          </div>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {data.cc_pending.per_account.map(a => (
              <div key={a.id} className="flex items-center justify-between bg-white/60 rounded px-2 py-1.5 text-[12px]">
                <span className="text-amber-900">
                  <span className="font-mono text-[10px] mr-1">{a.code}</span>{a.name}
                </span>
                <span className="tabular-nums font-medium text-amber-900">{fmtEurDetail(a.balance)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Footer mit Klassifikation ──────────────────────────── */}
      {data?.classification && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-3 text-[11px] text-[#6B7280]">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <span className="font-medium text-[#1A1A2E]">Klassifikations-Config</span>
              {' · '}Version {data.classification.version}
              {data.classification.source && ` · ${data.classification.source}`}
              {' · '}{data.classification.override_count} Overrides
              {data.classification.seeded_at && ` · Seed ${data.classification.seeded_at}`}
            </div>
            <div className="flex items-center gap-3 font-mono text-[10px]">
              <span>Banken: {data.classification.bank_codes.join(', ')}</span>
              <span>CC: {data.classification.cc_codes.join(', ')}</span>
              <span>Hidden-Intern: {data.classification.hidden_internal_codes.join(', ')}</span>
            </div>
          </div>
          {Array.isArray(data.classification.tag_bucket_map) && data.classification.tag_bucket_map.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="text-[10px] text-[#9CA3AF]">Odoo-Tag-Mapping:</span>
              {data.classification.tag_bucket_map.map(t => (
                <span key={t.tag_id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px]"
                  style={{ backgroundColor: BUCKET_META[t.bucket]?.tint, color: BUCKET_META[t.bucket]?.color }}>
                  <span className="font-mono">#{t.tag_id}</span>
                  <span>{t.tag_name}</span>
                  <span className="opacity-60">→ {BUCKET_META[t.bucket]?.label || t.bucket}</span>
                </span>
              ))}
            </div>
          )}
          {data.classification.note && (
            <div className="mt-1 text-[10px] italic text-[#9CA3AF]">{data.classification.note}</div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Wasserfall (analog #1180 EBITDA-Pattern) ─────────────────────
function WaterfallChart({ waterfallData }) {
  const barColor = (kind, bucket, signed) => {
    if (kind === 'total') return '#1A1A2E';
    if (kind === 'bucket') return signed >= 0 ? '#10B981' : '#EF4444';
    return '#338edc';
  };
  return (
    <div style={{ width: '100%', height: 320 }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={waterfallData} margin={{ top: 24, right: 16, left: 0, bottom: 4 }}>
          <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false}
            axisLine={{ stroke: '#E5E7EB' }} interval={0} />
          <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={false}
            tickFormatter={v => new Intl.NumberFormat('de-AT', { notation: 'compact', maximumFractionDigits: 1 }).format(v) + ' €'} />
          <ReferenceLine y={0} stroke="#9CA3AF" strokeWidth={1} />
          <Tooltip
            formatter={(_v, _n, ctx) => [fmtEurDetail(ctx.payload?.signed), ctx.payload?.name]}
            labelFormatter={() => ''}
            contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12 }}
            cursor={{ fill: 'rgba(51,142,220,0.06)' }}
          />
          <Bar dataKey="base" stackId="wf" fill="transparent" />
          <Bar dataKey="delta" stackId="wf" radius={[4, 4, 0, 0]}>
            {waterfallData.map((row, idx) => (
              <Cell key={idx} fill={barColor(row.kind, row.bucket, row.signed)} />
            ))}
            <LabelList dataKey="signed" position="top"
              formatter={(v) => fmtEur(v)}
              style={{ fontSize: 10, fill: '#1A1A2E' }} />
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function KpiTile({ label, value, sub, loading, Icon, iconColor, iconTint, highlight }) {
  return (
    <div className={`bg-white border ${highlight ? 'border-[#338edc] ring-1 ring-[#338edc]/20' : 'border-[#E5E7EB]'} rounded-xl p-4`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">{label}</div>
        {Icon && (
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg shrink-0"
            style={{ backgroundColor: iconTint || '#F3F4F6' }} aria-hidden="true">
            <Icon width={16} height={16} stroke={iconColor || '#6B7280'} strokeWidth={2} fill="none" />
          </span>
        )}
      </div>
      <div className="text-2xl font-semibold text-[#1A1A2E] tabular-nums">
        {loading ? <span className="inline-block w-24 h-7 rounded bg-[#F3F4F6] animate-pulse" /> : value}
      </div>
      {sub && <div className="text-[11px] text-[#9CA3AF] mt-1">{sub}</div>}
    </div>
  );
}

// ─── #1193 Cashflow-Brücke (horizontale Balken pro Bucket + Σ) ────────────────
// items: [{ key, label, value, color, tone: 'positive'|'negative'|'neutral', sub }]
// totalValue ist der Σ-Wert (blau dargestellt).
function CashflowBridge({
  title, subtitle, meta, loading,
  items, totalLabel, totalValue, totalSub,
  emptyHint, footerNote, detailsAccordion,
}) {
  // Balken-Skala: max(|value|) über alle Items + |total|. Mindestwert 1 um div/0 zu vermeiden.
  const scale = useMemo(() => {
    const vals = [...(items || []).map(i => Math.abs(i.value || 0)), Math.abs(totalValue || 0)];
    const max = vals.reduce((m, v) => Math.max(m, v), 0);
    return max > 0 ? max : 1;
  }, [items, totalValue]);

  const nonEmpty = (items || []).filter(i => Math.abs(i.value || 0) > 0.005);

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
      <div className="flex items-baseline justify-between mb-3 gap-2 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold text-[#1A1A2E]">
            {title}
            <span className="ml-2 text-[11px] font-normal text-[#9CA3AF]">· {subtitle}</span>
          </h2>
        </div>
        <div className="flex items-baseline gap-3">
          {meta && <span className="text-[11px] text-[#9CA3AF]">{meta}</span>}
          {totalValue != null && (
            <span className="text-base font-semibold text-[#338edc] tabular-nums">
              {totalValue >= 0 ? '+' : ''}{fmtEurDetail(totalValue)}
            </span>
          )}
        </div>
      </div>

      {loading ? (
        <div className="h-32 bg-[#F3F4F6] rounded animate-pulse" />
      ) : nonEmpty.length === 0 ? (
        <div className="text-sm text-[#9CA3AF] py-6 text-center">{emptyHint}</div>
      ) : (
        <div className="space-y-1.5">
          {nonEmpty.map(it => (
            <BridgeRow key={it.key || it.label} item={it} scale={scale} />
          ))}
          {/* Σ-Bar (blau, prominent) */}
          <div className="pt-2 mt-1 border-t border-[#E5E7EB]">
            <BridgeRow
              item={{
                key: '__total__',
                label: totalLabel,
                value: totalValue || 0,
                color: '#338edc',
                tone: 'total',
                sub: totalSub,
              }}
              scale={scale}
              isTotal
            />
          </div>
        </div>
      )}

      {footerNote && (
        <div className="text-[11px] text-[#9CA3AF] mt-3">{footerNote}</div>
      )}

      {detailsAccordion}
    </div>
  );
}

function BridgeRow({ item, scale, isTotal }) {
  const abs = Math.abs(item.value || 0);
  const widthPct = scale > 0 ? Math.min(100, (abs / scale) * 100) : 0;
  // Visuell: positiv geht nach rechts (Balken rechts der Mittellinie),
  // negativ nach links. Damit Auge sofort Vorzeichen erkennt.
  const isNegative = (item.value || 0) < 0;
  const isNeutral = item.tone === 'neutral';
  return (
    <div className="grid grid-cols-[170px_1fr_140px] gap-2 items-center text-sm py-1">
      <div className="flex items-center gap-2 min-w-0">
        <span
          className="inline-block w-2 h-2 rounded-full shrink-0"
          style={{ backgroundColor: item.color }}
          aria-hidden="true"
        />
        <div className="min-w-0">
          <div className={`truncate ${isTotal ? 'font-semibold text-[#1A1A2E]' : 'text-[#1A1A2E]'}`}>
            {item.label}
          </div>
          {item.sub && <div className="text-[10px] text-[#9CA3AF] truncate">{item.sub}</div>}
        </div>
      </div>
      {/* Balken: Mittellinie zentriert, positive rechts, negative links, neutral mittig */}
      <div className="relative h-[18px] bg-[#F8F9FC] rounded border border-[#F3F4F6]">
        {/* Mittellinie */}
        <div className="absolute top-0 bottom-0 left-1/2 w-px bg-[#E5E7EB]" aria-hidden="true" />
        {/* Balken */}
        {abs > 0 && (
          <div
            className="absolute top-[2px] bottom-[2px] rounded-sm"
            style={{
              backgroundColor: item.color,
              opacity: isNeutral ? 0.5 : (isTotal ? 0.95 : 0.85),
              left: isNegative || isNeutral && !isTotal && (item.value || 0) < 0
                ? `${50 - widthPct / 2}%`
                : '50%',
              width: `${widthPct / 2}%`,
              ...(isNeutral || isTotal ? {
                left: `${50 - widthPct / 2}%`,
                width: `${widthPct}%`,
              } : {}),
            }}
            aria-label={`${item.label}: ${fmtEurDetail(item.value)}`}
          />
        )}
      </div>
      <div
        className={`text-right tabular-nums ${isTotal ? 'font-semibold' : 'font-medium'}`}
        style={{ color: isTotal ? '#1A1A2E' : item.color }}
      >
        {(item.value || 0) >= 0 ? '+' : ''}{fmtEurDetail(item.value || 0)}
      </div>
    </div>
  );
}

// ─── #1193 Plan-Details-Accordion ─────────────────────────────────────────────
// Gruppiert die ~22 Plan-Positionen nach Bucket (operativ/investiv/finanzierend)
// + Saldos. Aggregate-Positionen fett. Roh-`cashflow_xxx`-Keys NICHT mehr sichtbar.
function PlanDetailsAccordion({ positions, labelsSource }) {
  const [open, setOpen] = useState(false);
  const grouped = useMemo(() => {
    const buckets = { operativ: [], investiv: [], finanzierend: [], saldo: [] };
    for (const p of positions) {
      if (p.kind === 'saldo' || !p.bucket) buckets.saldo.push(p);
      else if (buckets[p.bucket]) buckets[p.bucket].push(p);
      else buckets.saldo.push(p);
    }
    // Innerhalb jedes Buckets: details zuerst, aggregate zuletzt + fett.
    const sortDetailFirst = (a, b) => {
      const o = (a.kind === 'aggregate' ? 1 : 0) - (b.kind === 'aggregate' ? 1 : 0);
      if (o !== 0) return o;
      return Math.abs(b.total) - Math.abs(a.total);
    };
    Object.values(buckets).forEach(arr => arr.sort(sortDetailFirst));
    return buckets;
  }, [positions]);

  const sections = [
    { key: 'operativ', label: 'Operativ', color: BUCKET_META.operativ.color, tint: BUCKET_META.operativ.tint },
    { key: 'investiv', label: 'Investiv', color: BUCKET_META.investiv.color, tint: BUCKET_META.investiv.tint },
    { key: 'finanzierend', label: 'Finanzierend', color: BUCKET_META.finanzierend.color, tint: BUCKET_META.finanzierend.tint },
    { key: 'saldo', label: 'Salden / Kontrollzeilen', color: '#9CA3AF', tint: '#F3F4F6' },
  ];

  return (
    <div className="mt-3 border-t border-[#E5E7EB] pt-3">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="text-[12px] text-[#338edc] hover:text-[#1d6bbf] font-medium flex items-center gap-1"
        aria-expanded={open}
      >
        <span className="inline-block w-3 transition-transform" style={{ transform: open ? 'rotate(90deg)' : 'none' }}>
          ▶
        </span>
        {open ? 'Plan-Details ausblenden' : `Plan-Details anzeigen (${positions.length} Positionen)`}
      </button>
      {open && (
        <div className="mt-3 space-y-4">
          {sections.map(sec => {
            const rows = grouped[sec.key] || [];
            if (rows.length === 0) return null;
            return (
              <div key={sec.key}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: sec.color }} aria-hidden="true" />
                  <span className="text-[11px] uppercase tracking-wider font-medium" style={{ color: sec.color }}>
                    {sec.label}
                  </span>
                </div>
                <div className="space-y-0.5">
                  {rows.map(p => (
                    <div
                      key={p.position}
                      className={`flex items-center justify-between text-[12px] py-1 px-2 rounded ${
                        p.kind === 'aggregate' ? 'font-semibold' : ''
                      }`}
                      style={p.kind === 'aggregate' ? { backgroundColor: sec.tint } : undefined}
                    >
                      <span className="text-[#1A1A2E]">{p.label}</span>
                      <span
                        className="tabular-nums"
                        style={{ color: p.kind === 'saldo' ? '#1A1A2E' : (p.total >= 0 ? '#10B981' : '#EF4444') }}
                      >
                        {p.total >= 0 ? '+' : ''}{fmtEurDetail(p.total)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {labelsSource && (
            <div className="text-[10px] text-[#9CA3AF] italic">Labels: {labelsSource}</div>
          )}
        </div>
      )}
    </div>
  );
}

function RunwayTile({ runway, loading }) {
  if (loading) {
    return <KpiTile label="Runway" Icon={TrendingDown} iconColor="#9CA3AF" loading value="–" />;
  }
  if (!runway) return <KpiTile label="Runway" Icon={TrendingDown} iconColor="#9CA3AF" value="–" sub="Keine Daten" />;
  if (runway.months == null) {
    return (
      <KpiTile label="Runway" Icon={TrendingDown} iconColor="#10B981" iconTint="#ECFDF5"
        value="∞"
        sub={runway.note || 'Operativ positiv'} />
    );
  }
  const color = runway.months < 3 ? '#EF4444' : runway.months < 6 ? '#F59E0B' : '#10B981';
  const tint = runway.months < 3 ? '#FEF2F2' : runway.months < 6 ? '#FFFBEB' : '#ECFDF5';
  return (
    <KpiTile label="Runway" Icon={TrendingDown} iconColor={color} iconTint={tint}
      value={`${runway.months.toFixed(1)} Monate`}
      sub={runway.ceiling ? `bis ca. ${runway.ceiling}` : (runway.note || '')} />
  );
}
